<template>
  <div>
    <div class="mb-1">
      <v-breadcrumbs
        class="px-0"
        :items="accountEditBreadcrumbItems"
      >
      </v-breadcrumbs>
    </div>
    <div v-if="child">
      <div class="mb-8">
        <h1>{{ child.lastName }} {{ child.firstName }}</h1>
      </div>

      <div class="my-8">
        <h4>{{ $t('children.childcareType') }}</h4>
        <v-select
          variant="outlined"
          density="compact"
          hide-details
          :items="childTypeOptions"
          item-title="label"
          item-value="value"
          v-model="childType"
          color="primary"
        ></v-select>
      </div>

      <div class="my-8">
        <h4 class="mb-2">{{ $t('common.name') }}</h4>
        <v-row>
          <v-col cols="6">
            <v-text-field
              variant="outlined"
              density="compact"
              hide-details
              :label="$t('common.lastName')"
              :rules="[required]"
              v-model="lastName"
              color="primary"
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-text-field
              variant="outlined"
              density="compact"
              hide-details
              :label="$t('common.firstName')"
              :rules="[required]"
              v-model="firstName"
              color="primary"
            ></v-text-field>
          </v-col>
        </v-row>
      </div>

      <div class="my-8">
        <h4 class="mb-2">{{ $t('children.nurseryCodeNumber') }}</h4>
        <v-text-field
          variant="outlined"
          density="compact"
          hide-details
          :label="$t('children.optionalCode1')"
          v-model="code1"
          class="mb-2"
          color="primary"
        ></v-text-field>
        <v-text-field
          variant="outlined"
          density="compact"
          hide-details
          :label="$t('children.optionalCode2')"
          v-model="code2"
          color="primary"
        ></v-text-field>
      </div>

      <div class="my-8">
        <h4 class="mb-2">{{ $t('children.dateOfBirth') }}</h4>
        <date-picker
          v-model="dateOfBirth"
          :label="$t('children.dateOfBirth')"
        ></date-picker>
      </div>

      <div class="my-8">
        <h4 class="mb-2">{{ $t('common.gender') }}</h4>
        <v-select
          variant="outlined"
          density="compact"
          hide-details
          :items="genderOptions"
          item-title="label"
          item-value="value"
          v-model="gender"
          color="primary"
        ></v-select>
      </div>

      <div class="my-8">
        <h4 class="mb-2">{{ $t('children.admissionDate') }}</h4>
        <date-picker
          v-model="admissionDate"
          :label="$t('children.admissionDate')"
          clearable
        ></date-picker>
      </div>

      <div class="my-8">
        <h4 class="mb-2">{{ $t('children.graduationDate') }}</h4>
        <date-picker
          v-model="graduationDate"
          :label="$t('children.graduationDate')"
          clearable
        ></date-picker>
      </div>

      <div class="my-8">
        <h4 class="mb-2">{{ $t('children.withdrawalDate') }}</h4>
        <date-picker
          v-model="moveOrLeave"
          :label="$t('children.withdrawalDate')"
          clearable
        ></date-picker>

        <div v-if="moveOrLeave">
          <v-textarea
            variant="outlined"
            density="compact"
            hide-details
            full-width
            :label="$t('attendance.reason')"
            v-model="moveOrLeaveReason"
            color="primary"
          ></v-textarea>
        </div>
      </div>

      <div class="my-8">
        <h4>{{ $t('common.class') }}</h4>
        <v-select
          :items="classOptions"
          item-title="className"
          item-value="classId"
          v-model="classIds"
          multiple
          variant="outlined"
          density="compact"
          hide-details
          color="primary"
        >
        </v-select>
      </div>

      <div class="my-8">
        <h4>{{ $t('children.group') }}</h4>
        <v-select
          :items="groups || []"
          item-title="name"
          item-value="groupId"
          return-object
          v-model="groupIds"
          multiple
          variant="outlined"
          density="compact"
          hide-details
          color="primary"
        >
        </v-select>
      </div>

      <div class="my-8">
        <h4>{{ $t('children.extendedCarePattern') }}</h4>
        <v-select
          v-model="hoursPattern"
          :items="patternList"
          item-title="patternTitle"
          return-object
          variant="outlined"
          density="compact"
          hide-details="auto"
          color="primary"
        ></v-select>
      </div>

      <div class="my-8">
        <h4>{{ $t('children.additionalStaffForSpecialNeeds') }}</h4>
        <v-text-field
          v-model="additionalStaffNum"
          variant="outlined"
          density="compact"
          hide-details
          type="number"
          :min="0"
          color="primary"
        />
      </div>

      <div class="my-8">
        <h4>{{ $t('children.maxSubsidyAmount') }}</h4>
        <v-text-field
          v-model="maxSubsidyAmount"
          variant="outlined"
          density="compact"
          hide-details
          type="number"
          :min="0"
          color="primary"
        />
      </div>

      <!--【WS】園児予定のデフォルト登録を曜日ごとに登録できるように:対応でコメントアウト-->
      <!-- <div class="my-8">
        <h4>登園・降園のデフォルト設定</h4>
        <div class="my-4" style="width: 320px">
          <h4>登園時間</h4>
          <HourMinuteInput v-model="defaultStartMin" />
        </div>
        <div class="my-4" style="width: 320px">
          <h4>降園時間</h4>
          <HourMinuteInput v-model="defaultEndMin" />
        </div>
      </div> -->

      <div>
        <v-progress-linear
          v-if="isUpdating"
          indeterminate
          color="primary"
          class="my-8"
        ></v-progress-linear>
      </div>

      <div class="d-flex align-center">
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          variant="flat"
          x-large
          @click="update"
          :disabled="isUpdating"
          >{{ $t('common.save') }}</v-btn
        >
        <v-spacer></v-spacer>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";

import { childAccountModule } from "@/store/dataModules/childAccountModule";
import {
  ChildcareTypeType,
  GenderType,
} from "chaild-api/src/model/mysql/entity/childEntity";
import LocalDataService from "@/service/LocalDataService";
import HourMinuteInput from "@/components/molecules/HourMinuteInput.vue";
import { classModule } from "@/store/dataModules/classModule";
import {
  NurseryChildResponse,
  NurseryClassResponse,
  NurseryGroupListResponse,
  NurseryHoursPatternResponse,
} from "chaild-api/lib";
import { nurseryHoursModule } from "@/store/dataModules/nurseryHoursModule";
import ValidationMixin from "@/mixins/ValidationMixin";
import { groupModule } from "@/store/dataModules/groupModule";
import DatePicker from "@/components/atoms/DatePicker.vue";

interface DataType {
  isUpdating: boolean;
}

export default defineComponent({
  mixins: [ValidationMixin],

  components: {
    DatePicker,
  },

  //【WS】園児予定のデフォルト登録を曜日ごとに登録できるように:対応でコメントアウト
  // components: {
  //   HourMinuteInput,
  // },

  data: (): DataType => {
    return {
      isUpdating: false,
    };
  },

  computed: {
    accountEditBreadcrumbItems(): Array<{
      title: string;
      href?: string;
      color?: string;
      disabled: boolean;
    }> {
      const childId = this.$route.params.childId as string;
      const accountHref = childId ? `/children/account/${childId}` : undefined;
      const name = this.child
        ? `${this.child.lastName ?? ""} ${this.child.firstName ?? ""}`.trim()
        : "";
      const title = name || this.$t("common.loading");
      return [
        {
          title: this.$t("children.childrenParentsInfo"),
          href: "/children",
          color: "primary",
          disabled: false,
        },
        {
          title,
          href: accountHref,
          color: "primary",
          disabled: false,
        },
        { title: this.$t("common.edit"), disabled: true },
      ];
    },
    child(): NurseryChildResponse | null {
      return childAccountModule.child;
    },
    classes(): NurseryClassResponse[] {
      return classModule.classes;
    },
    classOptions(): any[] {
      return this.classes.map((c) => {
        return {
          classId: c.classId,
          className: `(${c.year}) ${c.className}`,
        };
      });
    },
    groups: function (): any[] {
      return groupModule.groups.map((g) => {
        return {
          groupId: g.groupId,
          name: `(${g.year}) ${g.name}`,
        };
      });
    },
    childType: {
      get(): string | null {
        if (this.child) {
          return this.child.childcareType;
        }
        return "";
      },
      set(type: ChildcareTypeType) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.childcareType = type;
          childAccountModule.setChild(newValue);
        }
      },
    },
    childTypeOptions() {
      return [
        { value: "normal", label: this.$t('children.regularChildcare') },
        { value: "temporary", label: this.$t('children.temporaryChildcare') },
      ];
    },
    classIds: {
      get(): number[] {
        if (this.child) {
          return this.child.nurseryClasses.map((c) => c.classId);
        }
        return [];
      },
      set(value: number[]) {
        if (this.child) {
          const newValue = { ...this.child };
          const classes: NurseryClassResponse[] = [...this.classes];
          const classObjects = value
            .map((id) => {
              const find = classes.find((c) => c.classId === id);
              if (find) {
                return find;
              }
              return null;
            })
            .filter((obj) => obj !== null) as NurseryClassResponse[];
          newValue.nurseryClasses = classObjects;
          childAccountModule.setChild(newValue);
        }
      },
    },
    groupIds: {
      get(): NurseryGroupListResponse {
        return this.child?.nurseryGroups || [];
      },
      set(value: NurseryGroupListResponse) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.nurseryGroups = value;
          childAccountModule.setChild(newValue);
        }
      },
    },
    firstName: {
      get(): string {
        if (this.child) {
          return this.child.firstName;
        }
        return "";
      },
      set(value: string) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.firstName = value.trim();
          childAccountModule.setChild(newValue);
        }
      },
    },
    firstNameKana: {
      get(): string | null {
        if (this.child) {
          return this.child.firstNameKana;
        }
        return "";
      },
      set(value: string | null) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.firstNameKana = value?.trim() ?? null;
          childAccountModule.setChild(newValue);
        }
      },
    },
    lastName: {
      get(): string {
        if (this.child) {
          return this.child.lastName;
        }
        return "";
      },
      set(value: string) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.lastName = value.trim();
          childAccountModule.setChild(newValue);
        }
      },
    },
    lastNameKana: {
      get(): string | null {
        if (this.child) {
          return this.child.lastNameKana;
        }
        return "";
      },
      set(value: string | null) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.lastNameKana = value?.trim() ?? null;
          childAccountModule.setChild(newValue);
        }
      },
    },
    code1: {
      get(): string | null {
        if (this.child) {
          return this.child.optionalChildCode1;
        }
        return "";
      },
      set(value: string | null) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.optionalChildCode1 = value;
          childAccountModule.setChild(newValue);
        }
      },
    },
    code2: {
      get(): string | null {
        if (this.child) {
          return this.child.optionalChildCode2;
        }
        return "";
      },
      set(value: string | null) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.optionalChildCode2 = value;
          childAccountModule.setChild(newValue);
        }
      },
    },
    dateOfBirth: {
      get(): string | null {
        if (this.child) {
          return this.child.dateOfBirth;
        }
        return "";
      },
      set(value: string) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.dateOfBirth = value;
          childAccountModule.setChild(newValue);
        }
      },
    },
    genderOptions() {
      return [
        { value: "male", label: this.$t('common.male') },
        { value: "female", label: this.$t('common.female') },
      ];
    },
    gender: {
      get(): string | null {
        if (this.child) {
          return this.child.gender;
        }
        return "";
      },
      set(value: GenderType) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.gender = value;
          childAccountModule.setChild(newValue);
        }
      },
    },
    admissionDate: {
      get(): string | null {
        if (this.child) {
          return this.child.admissionDate;
        }
        return "";
      },
      set(value: string) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.admissionDate = value;
          childAccountModule.setChild(newValue);
        }
      },
    },
    graduationDate: {
      get(): string | null {
        if (this.child) {
          return this.child.graduationDate;
        }
        return "";
      },
      set(value: string) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.graduationDate = value;
          childAccountModule.setChild(newValue);
        }
      },
    },
    moveOrLeave: {
      get(): string | null {
        if (this.child) {
          return this.child.quitDate;
        }
        return "";
      },
      set(value: string) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.quitDate = value;
          childAccountModule.setChild(newValue);
        }
      },
    },
    moveOrLeaveReason: {
      get(): string | null {
        if (this.child) {
          return this.child.quitReason;
        }
        return null;
      },
      set(value: string) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.quitReason = value;
          childAccountModule.setChild(newValue);
        }
      },
    },
    notaBene: {
      get(): string | null {
        if (this.child) {
          return this.child.note;
        }
        return null;
      },
      set(value: string) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.note = value;
          childAccountModule.setChild(newValue);
        }
      },
    },
    hoursPattern: {
      get(): NurseryHoursPatternResponse | null {
        if (this.child) {
          return this.child.hoursPattern;
        }
        return null;
      },
      set(value: NurseryHoursPatternResponse) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.hoursPattern = value;
          childAccountModule.setChild(newValue);
        }
      },
    },
    additionalStaffNum: {
      get(): number | null {
        if (this.child) {
          return this.child.additionalStaffNum;
        }
        return null;
      },
      set(value: number) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.additionalStaffNum = value;
          childAccountModule.setChild(newValue);
        }
      },
    },
    maxSubsidyAmount: {
      get(): number | null {
        if (this.child) {
          return this.child.maxSubsidyAmount;
        }
        return null;
      },
      set(value: number) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.maxSubsidyAmount = value;
          childAccountModule.setChild(newValue);
        }
      },
    },
    defaultStartMin: {
      get(): number | null {
        if (this.child) {
          return this.child.defaultStartMin;
        }
        return null;
      },
      set(value: number) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.defaultStartMin = value;
          childAccountModule.setChild(newValue);
        }
      },
    },
    defaultEndMin: {
      get(): number | null {
        if (this.child) {
          return this.child.defaultEndMin;
        }
        return null;
      },
      set(value: number) {
        if (this.child) {
          const newValue = { ...this.child };
          newValue.defaultEndMin = value;
          childAccountModule.setChild(newValue);
        }
      },
    },
    patternList: function (): NurseryHoursPatternResponse[] {
      return nurseryHoursModule.patternList;
    },
  },

  methods: {
    async update() {
      this.isUpdating = true;
      await childAccountModule.updateChild().then(() => {
        const childId = this.$route.params.childId as string;
        this.$router.push(`/children/account/${childId}`);
      });
      this.isUpdating = false;
    },
    async listNurseryHoursPattern() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await nurseryHoursModule.listPattern({
          nurseryId,
        });
      }
    },
    async listGroups() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await groupModule.listGroups({
          nurseryId,
        });
      }
    },
  },

  async mounted() {
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      await classModule.listClass({
        nurseryId: nurseryId,
      });
    }

    const childId = this.$route.params.childId as string;
    if (childId) {
      try {
        const parsed = parseInt(childId);
        childAccountModule.setChildId(parsed);
        childAccountModule.getChild();
        // childAccountModule.listAllergicFoods();
      } catch {
        return;
      }
    }

    await this.listNurseryHoursPattern();
    await this.listGroups();
  },

});
</script>
